import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import {
  Tag,
  Avatar,
  notification,
  Tooltip,
  Button,
  Input,
  Table,
  Select
} from 'antd';
import { Comment } from '@ant-design/compatible';
import { PageHeader } from '@ant-design/pro-layout';
import { useRecoilState, useRecoilValue, useRecoilValueLoadable, useSetRecoilState } from 'recoil';
import moment from 'moment';
import dayjs from 'dayjs';
import { CheckCircleOutlined, SyncOutlined } from '@ant-design/icons';
import { businessAtom, selectedLayawayAtom, reloadLayawaysAtom, timezoneAtom } from '../../../atoms/Atoms';
import api from '../../../api/api';
import Grocefy from '../../../assets/images/grocefyLogoAlone.png';
import { mapLayawayStatus, renderShortDate, renderShortDateTime, thousand, toCurrency } from '../../utils/functions';
import envConfig from '../../../envConfig';
import { businessesSelector } from '../../../atoms/Selectors';

function Layaways() {
  const { Option } = Select;
  const business = useRecoilValue(businessAtom);
  const timezone = useRecoilValue(timezoneAtom);
  const [currentPage, setCurrentPage] = useState(0);
  const { t } = useTranslation();
  const [reloadLayaways, setReloadLayaways] = useRecoilState(reloadLayawaysAtom);
  const setSelectedLayaway = useSetRecoilState(selectedLayawayAtom);
  const [lastSearch, setLastSearch] = useState('');
  const [size, setSize] = useState(20);
  const [filterStatus, setFilterStatus] = useState(0);
  const [loading, setLoading] = useState(false);
  const businesses = useRecoilValueLoadable(businessesSelector);
  const [selectedBusiness, setSelectedBusiness] = useState(business?.id);

  const [result, setResult] = useState({
    total: 0,
    layaways: []
  });

  function showMessage(message) {
    notification.open({
      message: '',
      description: (
        <Comment
          author={<span>Grocefy</span>}
          avatar={<Avatar src={Grocefy} alt="grocefy" />}
          content={
            <p className="text-sm">
              {message}
            </p>
          }
          datetime={
            <Tooltip title={moment().format('YYYY-MM-DD HH:mm:ss')}>
              <span>{moment().fromNow()}</span>
            </Tooltip>
          }
        />
      ),
    });
  }

  function search(page, query) {
    if (!loading) {
      setLastSearch(query);
      setLoading(true);
      api.post('layaway/search', {
        businessId: selectedBusiness,
        query,
        page,
        size,
        Status: filterStatus
      })
        .then((response) => {
          if (response.data.error) {
            showMessage(response.data.error);
          } else {
            setResult(response.data.data);
            console.log(response.data.data);
          }
        })
        .catch((error) => {
          showMessage(error.message);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }

  function getStatusColor(status, nextPaymentDate) {
    let color = '';
    if (status === 1) {
      color = 'green';
    } else if (status === 2 || status === 3) {
      color = '';
    } else if (status === 0) {
      let offset = envConfig.REACT_APP_TIMEZONE_OFFSET;
      if (!Number.isInteger(offset)) {
        offset = parseInt(offset, 10);
      }
      const now = dayjs(dayjs().format('YYYY-MM-DD')).add(-1 * offset, 'hours');
      const tomorrow = now.add(1, 'days');
      const diffNow = now.diff(nextPaymentDate, 'days');
      const diffTomorrow = tomorrow.diff(nextPaymentDate, 'days');
      if (diffNow === 0) {
        color = 'orange';
      } else if (diffTomorrow > 0) {
        color = 'red';
      }
    }
    return color;
  }

  function getStatusText(status, nextPaymentDate) {
    let text = t(mapLayawayStatus(status));
    if (status === 0) {
      let offset = envConfig.REACT_APP_TIMEZONE_OFFSET;
      if (!Number.isInteger(offset)) {
        offset = parseInt(offset, 10);
      }
      const now = dayjs(dayjs().format('YYYY-MM-DD')).add(-1 * offset, 'hours');
      const tomorrow = now.add(1, 'days');
      const diffNow = now.diff(nextPaymentDate, 'days');
      const diffTomorrow = tomorrow.diff(nextPaymentDate, 'days');
      if (diffNow === 0) {
        text = t('overdue_tomorrow');
      } else if (diffTomorrow > 0) {
        text = t('overdue');
      }
    }
    return text;
  }

  useEffect(() => {
    if (reloadLayaways) {
      setReloadLayaways(false);
      search(currentPage, lastSearch);
    }
  }, [reloadLayaways]);

  useEffect(() => {
    setCurrentPage(0);
    search(0, lastSearch);
  }, [size, filterStatus]);

  useEffect(() => {
    setCurrentPage(0);
    search(0, lastSearch);
  }, []);

  useEffect(() => {
    if (business) {
      setSelectedBusiness(business.id);
    } else {
      setSelectedBusiness('');
    }
  }, [business]);

  useEffect(() => {
    setCurrentPage(0);
    search(0, lastSearch);
  }, [selectedBusiness]);

  return (
    <div>
      <PageHeader
        title={t('layaways')}
        tags={
          <Tag
            color="#2db7f5"
            icon={<CheckCircleOutlined />}
          >
            {business ? business.name : t('all_stores')}
          </Tag>
        }
        extra={[
          <Button
            type="primary"
            onClick={() => {
              search(currentPage, lastSearch);
            }}
          >
            {t('reload')}
          </Button>
        ]}
      />
      <div className="flex space-x-2">
        <Input.Search
          className="w-full mb-1 mt-5"
          allowClear
          loading={loading}
          enterButton={t('search')}
          onSearch={(value) => {
            setCurrentPage(0);
            search(0, value);
          }}
        />
        {!business?.id && (
          <div>
            <p className="font-bold">{t('business')}:</p>
            <Select
              loading={businesses?.state === 'loading'}
              disabled={businesses?.state === 'loading' || businesses?.contents?.data?.data?.results?.length === 0}
              className="w-48"
              value={selectedBusiness}
              onChange={(value) => setSelectedBusiness(value)}
            >
              <Option
                key="any"
                value=""
                className="text-xs flex items-center"
              >
                {t('any')}
              </Option>
              {businesses.state === 'hasValue' && (
                <>
                  {_.map(_.sortBy(businesses.contents.data.data.results, (s) => s.name, 'asc'), (b) => (
                    <Option
                      value={b.id}
                      key={b.id}
                    >
                      {b.name}
                    </Option>
                  ))}
                </>
              )}
            </Select>
          </div>
        )}
        <div>
          <p className="font-bold">{t('status')}:</p>
          <Select
            disabled={loading}
            className="w-48"
            value={filterStatus}
            onChange={(value) => setFilterStatus(value)}
          >
            <Option
              key="any"
              value={0}
              className="text-xs flex items-center"
            >
              {t('any')}
            </Option>
            <Option
              key="completed"
              value={1}
              className="text-xs flex items-center"
            >
              {t('completed')}
            </Option>
            <Option
              key="pending"
              value={2}
              className="text-xs flex items-center"
            >
              {t('pending')}
            </Option>
            <Option
              key="overdue_tomorrow"
              value={5}
              className="text-xs flex items-center"
            >
              {t('overdue_tomorrow')}
            </Option>
            <Option
              key="overdue"
              value={3}
              className="text-xs flex items-center"
            >
              {t('overdue')}
            </Option>
            <Option
              key="cancelled"
              value={4}
              className="text-xs flex items-center"
            >
              {t('cancelled')}
            </Option>
          </Select>
        </div>
      </div>
      <Table
        bordered
        loading={loading}
        dataSource={result.layaways}
        pagination={{
          pageSize: size,
          showSizeChanger: true,
          defaultCurrent: 0,
          current: currentPage + 1,
          total: result?.total,
          onChange: (page, pageSize) => {
            if (page - 1 !== currentPage) {
              setCurrentPage(page - 1);
              search(page - 1, lastSearch);
            } else {
              setSize(pageSize);
            }
          }
        }}
        columns={[
          {
            title: '',
            key: 'mobile',
            align: 'center',
            className: 'text-xs md:hidden',
            render: (row) => (
              <div className="grid grid-cols-2 gap-4 shadow-xl p-2">
                <p className="col-span-1 text-left">{row?.code}</p>
                <div className="col-span-1 text-right">
                  <Tag color={getStatusColor(row?.status, row?.nextExpirationDate)}>{getStatusText(row?.status, row?.nextExpirationDate)}</Tag>
                </div>
                <p className="col-span-2">{row?.user?.completeName}</p>
                <div className="col-span-1 text-left">
                  <p className="font-bold">{t('last_payment_on')}</p>
                  <p>{row?.lastPaymentDate ? renderShortDateTime(row?.lastPaymentDate, timezone) : '-'}</p>
                </div>
                <div className="col-span-1 text-right">
                  <p className="font-bold">{t('total')}</p>
                  <p>{toCurrency(row?.total)} ({thousand(row?.items.length)})</p>
                </div>
                <div className="col-span-2">
                  <p className="font-bold">{t('next_payment_on')}</p>
                  <p>{row?.lastPaymentDate ? renderShortDate(row?.nextExpirationDate, timezone) : '-'}</p>
                </div>
                <Button
                  className="col-span-2"
                  size="small"
                  type="primary"
                  onClick={() => setSelectedLayaway(row)}
                >
                  {t('view')}
                </Button>
              </div>
            ),
          },
          {
            title: t('code'),
            key: 'code',
            align: 'center',
            className: 'hidden md:table-cell text-xs',
            render: (row) => (
              <span>{row?.code}</span>
            ),
          },
          {
            title: t('user'),
            key: 'user',
            align: 'center',
            className: 'hidden md:table-cell text-xs',
            render: (row) => (
              <span>{row?.user?.completeName}</span>
            ),
          },
          {
            title: t('status'),
            key: 'status',
            align: 'center',
            className: 'hidden md:table-cell text-xs',
            render: (row) => (
              <Tag color={getStatusColor(row?.status, row?.nextExpirationDate)}>{getStatusText(row?.status, row?.nextExpirationDate)}</Tag>
            ),
          },
          {
            title: t('last_payment_on'),
            key: 'user',
            align: 'center',
            className: 'hidden md:table-cell text-xs',
            render: (row) => (
              <span>{row?.lastPaymentDate ? renderShortDateTime(row?.lastPaymentDate, timezone) : '-'}</span>
            ),
          },
          {
            title: t('next_payment_on'),
            key: 'user',
            align: 'center',
            className: 'hidden md:table-cell text-xs',
            render: (row) => (
              <span>{row?.nextExpirationDate ? renderShortDate(row?.nextExpirationDate, timezone) : '-'}</span>
            ),
          },
          {
            title: t('total'),
            key: 'total',
            align: 'center',
            className: 'hidden md:table-cell text-xs',
            render: (row) => (
              <span>{toCurrency(row?.total)} ({thousand(row?.items.length)})</span>
            ),
          },
          {
            title: t('actions'),
            key: 'actions',
            align: 'center',
            className: 'hidden md:table-cell text-xs',
            render: (row) => (
              <Button
                size="small"
                type="primary"
                onClick={() => setSelectedLayaway(row)}
              >
                {t('view')}
              </Button>
            ),
          },
        ]}
      />
    </div>
  );
}

export default Layaways;
